import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'

import { IBDashboardFixedLayoutPage } from '@pages/IntroducingBroker/Dashboard/IBDashboardFixedLayoutPage'
import type { DashboardContextValue } from '@pages/IntroducingBroker/DashboardPoC/DashboardWrapper'
import { DashboardGrid } from '@pages/IntroducingBroker/DashboardPoC/components/DashboardGrid'

import { Body2, Heading3, Heading6 } from '@ui/Typography'
import { RichIcon } from '@ui/components/RichIcon'
import { MetricsIcon } from '@ui/icons/Metrics.icon'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { LayoutSelector } from './components/LayoutSelector/LayoutSelector'

import styles from './IBDashboardPage.module.scss'

export const IBDashboardPage: FC = () => {
  const { t } = useTranslation()
  const {
    isLoading,
    error,
    isMobile,
    currentLayoutId,
    visibleWidgets,
    canAddMoreLayouts,
    handleLayoutSelect,
    handleDeleteLayout,
    handleCreateLayout,
    layoutCRUDActionModal,
    createLayoutModal,
    handleEnterEditMode,
    layouts,
    defaultLayoutId,
    handleSetDefaultLayout,
    currentLayout,
  } = useOutletContext<DashboardContextValue>()

  if (isLoading) {
    return (
      <div className={styles.dashboardPage}>
        <Loading isLoading showLoadingIcon text={t('Dashboard.Loading')} />
      </div>
    )
  }

  return (
    <div className={styles.dashboardPage}>
      {error ? (
        <div className={styles.dashboardContent}>
          <div className={styles.emptyState}>
            <Heading3 className={styles.emptyStateTitle}>
              {t('Dashboard.FailedToLoadDashboard')}
            </Heading3>
            <Body2 className={styles.emptyStateText}>{error.message}</Body2>
          </div>
        </div>
      ) : (
        <>
          <Box flex align='center' justify='space-between' mt='x24' borderBottom='outline-low-em'>
            <Box pb='x8'>
              <Heading6 medium color='text-high-em'>
                {t('Tabs.Dashboard')}
              </Heading6>
            </Box>

            {!isMobile && (
              <Box pb='x8'>
                <LayoutSelector
                  layouts={layouts}
                  currentLayoutId={currentLayoutId}
                  onLayoutSelect={handleLayoutSelect}
                  onCreateNew={() =>
                    createLayoutModal.open({
                      onCreate: handleCreateLayout,
                      presetLayouts: layouts.filter((layout) => layout.isPreset),
                    })
                  }
                  onDeleteLayout={handleDeleteLayout}
                  canAddMore={canAddMoreLayouts}
                  onEditMode={handleEnterEditMode}
                  openLayoutActionModal={layoutCRUDActionModal.open}
                  defaultLayoutId={defaultLayoutId}
                  handleSetDefaultLayout={handleSetDefaultLayout}
                />
              </Box>
            )}
          </Box>

          <div className={styles.dashboardContent}>
            {visibleWidgets.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>
                  <RichIcon
                    icon={{ icon: MetricsIcon, color: 'text-primary-high-em' }}
                    shape='squircle'
                    bg='surface-primary-low-em'
                    size='x64'
                  />
                </div>
                <Heading3 className={styles.emptyStateTitle}>{t('Dashboard.NoWidgets')}</Heading3>
                <Body2 className={styles.emptyStateText}>
                  {t('Dashboard.EmptyStateTextForDashboard')}
                </Body2>
              </div>
            ) : currentLayout?.isPreset ? (
              <IBDashboardFixedLayoutPage widgets={visibleWidgets} isMobile={isMobile} />
            ) : (
              <div className={styles.dashboardGridHost}>
                <DashboardGrid widgets={visibleWidgets} isMobile={isMobile} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
